import { Box, Flex, ScrollArea, Text } from '@mantine/core';
import { TbChevronDown, TbChevronUp, TbSquareRoundedArrowLeft } from 'react-icons/tb';
import { useEffect, useMemo, useRef } from 'react';
import { idCreator } from '../../../../Project/utils/idCreator';

const QuestionRow = ({
  sectionKey,
  question,
  index,
  expandedQuestions,
  onToggleQuestion,
  onNavigate,
  isActive,
  innerRef
}) => {
  const qKey = `${sectionKey}::q-${index}`;
  const isOpen = expandedQuestions?.has?.(qKey);
  const handleClick = () => {
    onToggleQuestion?.(sectionKey, index);
    onNavigate?.({ sectionKey, questionIndex: index, shouldExpand: !isOpen });
  };

  return (
    <Flex direction="column" key={qKey} ref={innerRef}>
      <Flex
        id={`formpf-sidebar-question-toggle-${idCreator(qKey)}`}
        sx={{
          borderRadius: '4px',
          backgroundColor: isActive ? '#E7F5FF' : 'transparent',
          '&:hover': {
            backgroundColor: '#E7F5FF'
          }
        }}
        px="8px"
        align="center"
        justify="space-between"
        py="6px"
        component="button"
        onClick={handleClick}
      >
        <Text
          id={`formpf-sidebar-question-label-${idCreator(qKey)}`}
          fz="14px"
          c="blue.6"
          ta="left"
        >
          {question.name}
        </Text>
        {isOpen ? (
          <TbChevronUp color="#1971C2" size={16} style={{ minWidth: '16px' }} />
        ) : (
          <TbChevronDown color="#1971C2" size={16} style={{ minWidth: '16px' }} />
        )}
      </Flex>
    </Flex>
  );
};

const SectionBlock = ({
  section,
  expandedSections,
  expandedQuestions,
  onToggleSection,
  onToggleQuestion,
  onNavigate,
  activeQuestionKey,
  registerItemRef
}) => {
  const sectionKey = section.key || section.name;
  const isOpen = expandedSections?.has?.(sectionKey);
  const handleClick = () => {
    onToggleSection?.(sectionKey);
    onNavigate?.({ sectionKey, shouldExpand: !isOpen });
  };

  return (
    <Flex direction="column" key={sectionKey}>
      <Flex
        id={`formpf-sidebar-section-toggle-${idCreator(sectionKey)}`}
        align="center"
        justify="space-between"
        py="6px"
        px="8px"
        component="button"
        sx={{
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#E7F5FF'
          }
        }}
        onClick={handleClick}
      >
        <Text id={`formpf-sidebar-section-label-${idCreator(sectionKey)}`} fz="14px" c="blue.6">
          {section.name}
        </Text>
        {section.questions && section.questions.length > 0 && (
          <>
            {isOpen ? (
              <TbChevronUp color="#1971C2" size={16} />
            ) : (
              <TbChevronDown color="#1971C2" size={16} />
            )}
          </>
        )}
      </Flex>
      {isOpen && (
        <Box pl="16px">
          {(() => {
            const questionsByName = new Map();
            (section.questions || []).forEach(q => {
              const name = q?.name || 'Question';
              if (!questionsByName.has(name)) {
                questionsByName.set(name, { name, tableNames: [] });
              }
              questionsByName.get(name).tableNames.push(q.tableName);
            });
            const mergedQuestions = Array.from(questionsByName.values());
            return mergedQuestions.map((mergedQ, idx) => {
              const key = `${sectionKey}::q-${idx}`;
              return (
                <QuestionRow
                  key={key}
                  sectionKey={sectionKey}
                  question={mergedQ}
                  index={idx}
                  expandedQuestions={expandedQuestions}
                  onToggleQuestion={onToggleQuestion}
                  onNavigate={onNavigate}
                  isActive={activeQuestionKey?.questionKey === key}
                  innerRef={registerItemRef(key)}
                />
              );
            });
          })()}
        </Box>
      )}
    </Flex>
  );
};

const extractQuestionNumber = questionName => {
  const match = questionName?.match(/^(\d+)\./);
  return match ? match[1] : null;
};

const CompactQuestionItem = ({
  questionNumber,
  sectionKey,
  questionIndex,
  isActive,
  hasError,
  onNavigate,
  innerRef
}) => {
  const handleClick = () => {
    onNavigate?.({ sectionKey, questionIndex, shouldExpand: true });
  };

  return (
    <Flex
      component="button"
      id={`formpf-sidebar-compact-question-toggle-${idCreator(sectionKey)}-${questionIndex}`}
      onClick={handleClick}
      align="center"
      justify="center"
      mih="32px"
      w="32px"
      ref={innerRef}
      sx={{
        borderRadius: '8px',
        border: hasError ? '1px solid #DF4141' : '1px solid #228BE6',
        backgroundColor: isActive ? '#E7F5FF' : 'white',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          backgroundColor: isActive ? '#D0EBFF' : '#F0F9FF'
        }
      }}
    >
      <Text
        id={`formpf-sidebar-compact-question-number-${idCreator(`${sectionKey}-${questionIndex}`)}`}
        fz="12px"
        fw={500}
        c="#495057"
      >
        {questionNumber}
      </Text>
    </Flex>
  );
};

export function Sidebar({
  id,
  isStaticTemplate,
  isExpandedQuickNavigation,
  toggleQuickNavigation,
  funds,
  currentDataKey,
  sections = [],
  expandedSections,
  expandedQuestions,
  activeQuestionKey: controlledActiveQuestionKey,
  onToggleSection,
  onToggleQuestion,
  onNavigate
}) {
  const itemRefs = useRef({});

  const allQuestions = useMemo(() => {
    const questions = [];
    sections.forEach(section => {
      const sectionKey = section.key || section.name;
      const questionsByName = new Map();
      (section.questions || []).forEach(q => {
        const name = q?.name || 'Question';
        if (!questionsByName.has(name)) {
          questionsByName.set(name, { name, tableNames: [] });
        }
        questionsByName.get(name).tableNames.push(q.tableName);
      });
      const mergedQuestions = Array.from(questionsByName.values());
      mergedQuestions.forEach((mergedQ, idx) => {
        const questionNumber = extractQuestionNumber(mergedQ.name);
        if (questionNumber) {
          questions.push({
            questionNumber,
            sectionKey,
            questionIndex: idx,
            question: mergedQ,
            key: `${sectionKey}::q-${idx}`
          });
        }
      });
    });
    return questions;
  }, [sections]);

  const questionErrors = useMemo(() => {
    if (!funds || !currentDataKey || !funds[currentDataKey]?.report_status?.errors) {
      return new Set();
    }
    const reportErrors = funds[currentDataKey].report_status.errors;
    const questionKeysByTable = new Map();

    allQuestions.forEach(q => {
      const tableNames =
        q.question.tableNames || (q.question.tableName ? [q.question.tableName] : []);
      tableNames.forEach(tableName => {
        if (!questionKeysByTable.has(tableName)) {
          questionKeysByTable.set(tableName, new Set());
        }
        questionKeysByTable.get(tableName).add(q.key);
      });
    });

    const errors = new Set();
    reportErrors.forEach(error => {
      const refKey = error.reference_key || '';
      questionKeysByTable.forEach((questionKeys, tableName) => {
        if (refKey.includes(tableName) || refKey === tableName) {
          questionKeys.forEach(questionKey => errors.add(questionKey));
        }
      });
    });

    return errors;
  }, [funds, currentDataKey, allQuestions]);

  const activeQuestionKey = useMemo(() => {
    if (controlledActiveQuestionKey) return controlledActiveQuestionKey;
    if (!expandedQuestions || expandedQuestions.size === 0) return null;
    const keys = Array.from(expandedQuestions);
    return keys[keys.length - 1];
  }, [controlledActiveQuestionKey, expandedQuestions]);

  const registerItemRef = key => el => {
    if (el) {
      itemRefs.current[key] = el;
    }
  };

  useEffect(() => {
    if (!activeQuestionKey) return;
    const node = itemRefs.current[activeQuestionKey?.questionKey];
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [activeQuestionKey, isExpandedQuickNavigation]);

  return (
    <Box id={id ?? 'formpf-sidebar'} pos="relative">
      <ScrollArea
        id="formpf-sidebar-scroll-area"
        scrollbarSize={14}
        scrollHideDelay={500}
        p={isExpandedQuickNavigation ? '24px 12px' : '12px 8px'}
        bg="white"
        h="100%"
        sx={() => ({
          position: 'sticky',
          top: '16px',
          minWidth: !isExpandedQuickNavigation ? '68px' : '272px',
          maxWidth: !isExpandedQuickNavigation ? '68px' : '272px',
          borderRadius: '4px',
          border: '1px solid #f1f3f5',
          overflowY: 'auto',
          transition: '0.4s ease-in-out all'
        })}
      >
        <Flex
          align="center"
          justify={isExpandedQuickNavigation ? 'space-between' : 'center'}
          mb={isExpandedQuickNavigation ? '16px' : '12px'}
          p="0 8px"
        >
          {isExpandedQuickNavigation && (
            <Text id="formpf-sidebar-title" fz="16px" lh="22px" c="gray.7">
              Quick Navigation
            </Text>
          )}
          <button
            id="formpf-sidebar-collapse-toggle"
            display="flex"
            onClick={toggleQuickNavigation}
            style={{
              transform: !isExpandedQuickNavigation && 'rotateZ(180deg)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <TbSquareRoundedArrowLeft color="#DEE2E6" fontSize="20px" />
          </button>
        </Flex>

        {!isExpandedQuickNavigation ? (
          <Flex direction="column" gap="8px" align="center">
            {allQuestions.map(q => (
              <CompactQuestionItem
                key={q.key}
                questionNumber={q.questionNumber}
                sectionKey={q.sectionKey}
                questionIndex={q.questionIndex}
                isActive={activeQuestionKey?.questionKey === q.key}
                hasError={questionErrors.has(q.key)}
                onNavigate={onNavigate}
                innerRef={registerItemRef(q.key)}
              />
            ))}
          </Flex>
        ) : (
          sections.map(section => (
            <SectionBlock
              key={section.key || section.name}
              section={section}
              expandedSections={expandedSections}
              expandedQuestions={expandedQuestions}
              onToggleSection={onToggleSection}
              onToggleQuestion={onToggleQuestion}
              onNavigate={onNavigate}
              activeQuestionKey={activeQuestionKey}
              registerItemRef={registerItemRef}
            />
          ))
        )}
      </ScrollArea>
    </Box>
  );
}
